import type { ReactNode } from "react";

/** Shared Google Appointment Schedule link used by every "book a call" CTA. */
export const BOOKING_URL = "https://calendar.app.google/gqBLsanJBhuNAAB6A";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { ConsentBanner } from "./ConsentBanner";

export function Layout({ children, linkSeoDescription = false }: { children: ReactNode; linkSeoDescription?: boolean }) {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer linkSeoDescription={linkSeoDescription} />
      <ConsentBanner />
    </div>
  );
}

export function Section({ children, className = "", id }: { children: ReactNode; className?: string; id?: string }) {
  return (
    <section id={id} className={`mx-auto max-w-7xl px-5 py-14 sm:px-6 sm:py-20 ${className}`}>{children}</section>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]"><span className="h-px w-8 bg-[var(--gold)]" />{children}</p>;
}

export function CTAButton({ to, children, variant = "primary" }: { to: string; children: ReactNode; variant?: "primary" | "ghost" | "gold" | "ghostLight" }) {
  const cls =
    variant === "primary"
      ? "bg-primary text-primary-foreground hover:bg-[var(--navy-deep)]"
      : variant === "gold"
      ? "bg-[var(--gold)] text-[var(--navy-deep)] hover:bg-[var(--gold-deep)]"
      : variant === "ghostLight"
      ? "border border-white/30 text-white hover:bg-white/10"
      : "border border-primary/30 text-primary hover:bg-primary/5";
  // External links (http/https) open in a new tab; internal paths navigate in-app.
  const isExternal = /^https?:\/\//i.test(to);
  return (
    <a
      href={to}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className={`inline-flex w-full items-center justify-center gap-2 rounded-md px-6 py-3 text-center text-sm font-semibold transition sm:w-auto ${cls}`}
    >
      {children}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
    </a>
  );
}

export function Breadcrumbs({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
      <ol className="flex flex-wrap items-center gap-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-1">
            {it.href ? <a href={it.href} className="hover:text-foreground">{it.label}</a> : <span className="text-foreground">{it.label}</span>}
            {i < items.length - 1 && <span>/</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function FinalCTA({ title = "Ready to dominate the search results in your market?", sub = "Get a free, no-obligation SEO audit. We'll show you exactly what's costing you cases, and how to fix it.", auditLabel = "Get my free SEO audit", auditTo = "/law-firm-seo-audit", strategyLabel = "Book a strategy call", strategyTo = BOOKING_URL }: { title?: string; sub?: string; auditLabel?: string; auditTo?: string; strategyLabel?: string; strategyTo?: string }) {
  return (
    <section className="bg-hero-gradient text-primary-foreground">
      <div className="mx-auto max-w-5xl px-5 py-14 text-center sm:px-6 sm:py-20">
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold mb-5">{title}</h2>
        <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto mb-8">{sub}</p>
        <div className="flex flex-col gap-3 justify-center sm:flex-row sm:flex-wrap">
        <CTAButton to={auditTo} variant="gold">{auditLabel}</CTAButton>
        {strategyLabel !== "" && <CTAButton to={strategyTo} variant="ghostLight">{strategyLabel}</CTAButton>}
        </div>
      </div>
    </section>
  );
}